/**
 * The per-render trace scope, published on `globalThis` rather than exported directly.
 *
 * The indirection is deliberate. `__root.tsx`'s `head()` needs to read the current render's trace ids
 * so it can bake them into the HTML, but `__root.tsx` is part of the CLIENT bundle too — importing a
 * module that pulls in `node:async_hooks` would break it. Reading an optional global instead keeps the
 * client side of the file free of any server import, and simply yields `undefined` in the browser.
 *
 * This file is isomorphic and must stay free of server-only imports. The store itself is created and
 * published by `src/server.ts` (server-only).
 */
export interface SsrTrace {
	traceId: string;
	rootSpanId: string;
}

interface SsrTraceStore {
	getStore(): SsrTrace | undefined;
}

/**
 * The trace ids for the render currently in progress, or undefined outside an SSR render (which
 * includes every call made from the browser).
 */
export function currentSsrTrace(): SsrTrace | undefined {
	return (globalThis as { __ssrTraceAls?: SsrTraceStore }).__ssrTraceAls?.getStore();
}

/**
 * The trace ids of the document being rendered: the in-progress render's ids on the server, and on the
 * client the ids that same render baked into the HTML.
 *
 * Reading them back in the browser is what keeps `head()` isomorphic. A script the server renders and
 * the client doesn't shifts every later head entry by one position, so React hydrates the analytics
 * `<script src>` against the inline bootstrap script and rejects the whole tree.
 */
export function currentDocumentTrace(): SsrTrace | undefined {
	const ssrTrace = currentSsrTrace();
	if (ssrTrace) return ssrTrace;
	if (typeof window === 'undefined') return undefined;
	const { __TRACE_ID: traceId, __ROOT_SPAN_ID: rootSpanId } = window;
	return traceId && rootSpanId ? { traceId, rootSpanId } : undefined;
}
